import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { useMappingProjectSessionStore } from './useMappingProjectSessionStore';
import type { Screen } from '../../../shared/types';
import {
  SettingsType,
  getActivePipeexProfile,
  getPipeexSettingsProfiles,
  setActivePipeexProfile,
  type SettingInfoDto,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import type { MappingConfiguration } from '../../../shared/types/config/mapping.types';
import {
  clearLiveWizardMappingHandoffIntent,
  markReinitiatedWizardMappingHandoffIntent,
  RESUME_INTO_MAPPING_KEY,
} from '../../../shared/lib/workflowResumeIntent';
import { useProjectSetupHistoryStore } from '../../../shared/stores/useProjectSetupHistoryStore';
import { buildProfileKey, parseSettingsType } from '../lib/profile';
import { resolveProfileSelection } from '../lib/profileSelectionResolution';
import { warnMappingFlow } from '../lib/mappingTrace';
import { resolveConnectorDocumentIdentity } from '../../../shared/lib/connectorTarget';
import type { ActiveDocumentInfo } from '../../../shared/hooks/useActiveDocumentName';
import {
  debugMappingProfile,
  elapsedMs,
  nowMs,
  startProfileStallWarning,
} from './profileControllerShared';
import { useFlowSnapshotPersistence } from './useFlowSnapshotPersistence';
import { useProfileRestoreOnMount } from './useProfileRestoreOnMount';
import { useProfileLoadActions } from './useProfileLoadActions';
import { useProfileCrudActions } from './useProfileCrudActions';
import { useVerifiedMappingProfileSave } from './useVerifiedMappingProfileSave';

interface UseMappingProfileControllerArgs {
  activeUserId: string | null;
  activeProjectKey: string | null;
  saveConfiguration: (profileName?: string, settingsType?: SettingsType) => Promise<void> | void;
  setGeneralSettings: Dispatch<SetStateAction<MappingConfiguration['general']>>;
  setAdvancedSettings: Dispatch<SetStateAction<MappingConfiguration['advanced']>>;
  markWorkflowApplied: () => void;
  onContinue: () => void;
  reinitiateWorkflow: () => void;
  jumpTo: (screen: Screen, history: Screen[]) => void;
  enableFlowSnapshot?: boolean;
  profileActivationReady?: boolean;
  requireExplicitProfileSelection?: boolean;
  activateProfileOnSelection?: boolean;
  restoreExistingProfileOnMount?: boolean;
  /** Identifies the document a persisted profile is verified against. */
  activeDocument?: ActiveDocumentInfo | null;
  projectPreferredProfile?: {
    name: string;
    settingsType: SettingsType;
    profileKey: string;
  } | null;
  deferProjectPreferredRestore?: boolean;
  onProfileConfirmed?: (profile: {
    name: string;
    settingsType: SettingsType;
    profileKey: string;
  }) => void;
  onFlowSnapshotPersisted?: (profile: {
    name: string;
    settingsType: SettingsType;
    profileKey: string;
  }) => void;
}


export const useMappingProfileController = ({
  activeUserId,
  activeProjectKey,
  saveConfiguration,
  setGeneralSettings,
  setAdvancedSettings,
  markWorkflowApplied,
  onContinue,
  reinitiateWorkflow,
  jumpTo,
  enableFlowSnapshot = true,
  profileActivationReady = true,
  requireExplicitProfileSelection = false,
  activateProfileOnSelection = true,
  restoreExistingProfileOnMount = false,
  activeDocument = null,
  projectPreferredProfile = null,
  deferProjectPreferredRestore = false,
  onProfileConfirmed,
  onFlowSnapshotPersisted,
}: UseMappingProfileControllerArgs) => {
  const markProjectSetupCompleted = useProjectSetupHistoryStore(
    (state) => state.markCompleted
  );
  const clearProjectSetupCompleted = useProjectSetupHistoryStore(
    (state) => state.clearCompleted
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<SettingInfoDto[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfileKey, setSelectedProfileKey] = useState('');
  const [selectedSettingsType, setSelectedSettingsType] = useState<SettingsType>(SettingsType.User);
  const [profileError, setProfileError] = useState<string | null>(null);
  const lastAppliedProfileKey = useMappingProjectSessionStore(
    (state) => state.lastAppliedProfileKey
  );
  const setLastAppliedProfileKey = useMappingProjectSessionStore(
    (state) => state.setLastAppliedProfileKey
  );
  const selectedProfileKeyRef = useRef('');
  const loadProfilesInFlightRef = useRef<Promise<void> | null>(null);
  const profilesRef = useRef<SettingInfoDto[]>([]);
  const flowSnapshotSavedRef = useRef(false);
  const flowSnapshotPersistingRef = useRef(false);
  const flowSnapshotSupersededRef = useRef(false);
  // Names the profile the flow snapshot has claimed but storage has not yet
  // confirmed. It outlives the persist so a rejected read-back cannot be
  // activated afterwards by anything that only watches the selection.
  const flowSnapshotUnverifiedKeyRef = useRef<string | null>(null);

  // `null` when the document is only partially known: a half-identity would let
  // a profile from another document pass verification.
  const setupIdentity = useMemo(
    () =>
      resolveConnectorDocumentIdentity({
        projectKey: activeDocument?.projectKey ?? null,
        documentName: activeDocument?.documentName ?? null,
        documentPath: activeDocument?.documentPath ?? null,
      }),
    [activeDocument?.projectKey, activeDocument?.documentName, activeDocument?.documentPath]
  );

  const handleSelectedProfileChange = useCallback((profileKey: string) => {
    if (flowSnapshotPersistingRef.current) {
      flowSnapshotSupersededRef.current = true;
    }
    // An explicit selection replaces whatever the flow snapshot had claimed.
    flowSnapshotUnverifiedKeyRef.current = null;
    debugMappingProfile('[MappingProfileController] selected profile changed', {
      previousProfileKey: selectedProfileKeyRef.current,
      nextProfileKey: profileKey,
    });
    selectedProfileKeyRef.current = profileKey;
    setSelectedProfileKey(profileKey);
    if (!profileKey) {
      return;
    }

    const [, settingsTypeRaw] = profileKey.split('::');
    setSelectedSettingsType(parseSettingsType(settingsTypeRaw));
  }, [flowSnapshotPersistingRef, flowSnapshotSupersededRef]);

  const applyProfileList = useCallback((
    data: SettingInfoDto[],
    currentSelectedProfileKey: string,
    activeProfile = getActivePipeexProfile()
  ) => {
      setProfiles(data);
      profilesRef.current = data;

      const resolution = resolveProfileSelection({
        profiles: data,
        currentSelectedProfileKey,
        activeProfileKey: activeProfile?.name
          ? buildProfileKey(activeProfile.name, activeProfile.settingsType)
          : '',
        projectPreferredProfileKey: projectPreferredProfile?.profileKey ?? '',
        requireExplicitProfileSelection,
        enableFlowSnapshot,
        restoreExistingProfileOnMount,
        flowSnapshotSaved: flowSnapshotSavedRef.current,
      });

      if (resolution.kind === 'empty-list') {
        selectedProfileKeyRef.current = '';
        setSelectedProfileKey('');
        return;
      }

      if (resolution.kind === 'cleared-for-project-gate') {
        debugMappingProfile(
          '[MappingProfileController] loadProfiles left selection empty for project gate',
          resolution.debugPayload
        );
        selectedProfileKeyRef.current = '';
        setSelectedProfileKey('');
        setSelectedSettingsType(SettingsType.User);
        return;
      }

      debugMappingProfile(
        '[MappingProfileController] loadProfiles resolved selection',
        resolution.debugPayload
      );
      selectedProfileKeyRef.current = resolution.nextProfileKey;
      setSelectedProfileKey(resolution.nextProfileKey);

      const [, settingsTypeRaw] = resolution.nextProfileKey.split('::');
      setSelectedSettingsType(parseSettingsType(settingsTypeRaw));
  }, [
    enableFlowSnapshot,
    flowSnapshotSavedRef,
    projectPreferredProfile,
    requireExplicitProfileSelection,
    restoreExistingProfileOnMount,
  ]);

  const loadProfiles = useCallback((): Promise<void> => {
    if (!profileActivationReady) {
      debugMappingProfile('[MappingProfileController] loadProfiles skipped', {
        reason: 'profileActivationNotReady',
      });
      return Promise.resolve();
    }

    if (loadProfilesInFlightRef.current) {
      debugMappingProfile('[MappingProfileController] loadProfiles reused in-flight request');
      return loadProfilesInFlightRef.current;
    }

    const currentSelectedProfileKey = selectedProfileKeyRef.current;
    const activeProfile = getActivePipeexProfile();
    const startedAt = nowMs();
    let stallTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
    const request = (async () => {
      setProfilesLoading(true);
      setProfileError(null);
      stallTimer = startProfileStallWarning(
        'profile list load still pending',
        {
          currentSelectedProfileKey,
          activeProfile,
          requireExplicitProfileSelection,
        },
        startedAt
      );
      debugMappingProfile('[MappingProfileController] loadProfiles start', {
        currentSelectedProfileKey,
        activeProfile,
        requireExplicitProfileSelection,
      });
      const data = await getPipeexSettingsProfiles();
      debugMappingProfile('[MappingProfileController] loadProfiles API success', {
        elapsedMs: elapsedMs(startedAt),
        profileCount: data.length,
        profileKeys: data.map((profile) => buildProfileKey(profile.name, profile.settingsType)),
      });
      applyProfileList(data, currentSelectedProfileKey, activeProfile);
    })()
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Failed to load settings profiles';
        warnMappingFlow('profile list load failed', {
          elapsedMs: elapsedMs(startedAt),
          currentSelectedProfileKey,
          activeProfile,
          error: message,
        });
        setProfileError(message);
      })
      .finally(() => {
        if (stallTimer) {
          globalThis.clearTimeout(stallTimer);
        }
        debugMappingProfile('[MappingProfileController] loadProfiles complete', {
          elapsedMs: elapsedMs(startedAt),
        });
        setProfilesLoading(false);
        if (loadProfilesInFlightRef.current === request) {
          loadProfilesInFlightRef.current = null;
        }
      });

    loadProfilesInFlightRef.current = request;
    return request;
  }, [
    applyProfileList,
    profileActivationReady,
    requireExplicitProfileSelection,
  ]);

  useEffect(() => {
    selectedProfileKeyRef.current = selectedProfileKey;
  }, [selectedProfileKey]);

  useEffect(() => {
    if (!activateProfileOnSelection || !selectedProfileKey) {
      return;
    }

    // A flow snapshot selection is only a claim until its read-back verifies:
    // every selection made while it persists is provisional, and the profile it
    // claimed stays untrusted afterwards unless the read-back confirmed it.
    const persistInFlight =
      flowSnapshotPersistingRef.current && !flowSnapshotSupersededRef.current;
    const claimUnverified = flowSnapshotUnverifiedKeyRef.current === selectedProfileKey;
    if (persistInFlight || claimUnverified) {
      debugMappingProfile('[MappingProfileController] selected profile activation deferred', {
        selectedProfileKey,
        reason: persistInFlight ? 'flow-snapshot-persisting' : 'flow-snapshot-unverified',
      });
      return;
    }

    const [name, settingsTypeRaw] = selectedProfileKey.split('::');
    if (!name) {
      return;
    }

    const settingsType = parseSettingsType(settingsTypeRaw);
    debugMappingProfile('[MappingProfileController] selected profile activated', {
      selectedProfileKey,
      name,
      settingsType,
    });
    setActivePipeexProfile(name, settingsType);
  }, [activateProfileOnSelection, selectedProfileKey]);

  const { handleApply, handleContinue } = useVerifiedMappingProfileSave({
    activeUserId,
    activeProjectKey,
    saveConfiguration,
    setupIdentity,
    selectedProfileKeyRef,
    setIsSaving,
    setSaveError,
    markWorkflowApplied,
    markProjectSetupCompleted,
    onProfileConfirmed,
    onContinue,
  });

  useEffect(() => {
    void loadProfiles();
  }, [loadProfiles]);

  useFlowSnapshotPersistence({
    enableFlowSnapshot,
    profileActivationReady,
    requireExplicitProfileSelection,
    restoreExistingProfileOnMount,
    identity: setupIdentity,
    onFlowSnapshotPersisted,
    loadProfiles,
    loadProfilesInFlightRef,
    profilesRef,
    selectedProfileKeyRef,
    flowSnapshotSavedRef,
    flowSnapshotPersistingRef,
    flowSnapshotSupersededRef,
    flowSnapshotUnverifiedKeyRef,
    setSelectedProfileKey,
    setSelectedSettingsType,
    setLastAppliedProfileKey,
    setProfileError,
  });

  useProfileRestoreOnMount({
    profileActivationReady,
    requireExplicitProfileSelection,
    restoreExistingProfileOnMount,
    deferProjectPreferredRestore,
    projectPreferredProfile,
    selectedProfileKey,
    selectedProfileKeyRef,
    setSelectedProfileKey,
    setSelectedSettingsType,
    setLastAppliedProfileKey,
    setIsSaving,
    setSaveError,
    setProfileError,
    setGeneralSettings,
    setAdvancedSettings,
  });

  const { profileApplyProgress, clearProfileApplyProgress, onLoadProfile } =
    useProfileLoadActions({
      selectedProfileKey,
      selectedProfileKeyRef,
      setSelectedProfileKey,
      setSelectedSettingsType,
      setLastAppliedProfileKey,
      setIsSaving,
      setSaveError,
      setProfileError,
      setGeneralSettings,
      setAdvancedSettings,
      loadProfiles,
      onProfileConfirmed,
    });

  const handleReinitiateWorkflow = useCallback(async () => {
    // Make sure the configuration store reflects the currently selected profile
    // before restarting the wizard. Otherwise a profile that differs from the
    // in-memory mapping table falls back to PipeEX defaults when reinitiated.
    if (selectedProfileKey) {
      const loaded = await onLoadProfile(selectedProfileKey);
      if (!loaded) {
        // Surface the load error and do not restart the wizard with stale settings.
        return;
      }
    }
    reinitiateWorkflow();
    useUIStore.getState().setDefaultPropertiesConfirmed(false);
    // A reinitiated wizard re-asks the standard-parameters prompt: clear
    // the persisted answer so it can be included in the next profile save.
    useConfigurationStore.getState().setIncludeDefaultPipeexParameters(null);
    clearProjectSetupCompleted(activeUserId, activeProjectKey);
    try {
      sessionStorage.removeItem(RESUME_INTO_MAPPING_KEY);
    } catch {
      // Ignore session storage failures.
    }
    clearLiveWizardMappingHandoffIntent();
    markReinitiatedWizardMappingHandoffIntent();
    jumpTo('menu', ['welcome', 'menu']);
  }, [
    activeProjectKey,
    activeUserId,
    clearProjectSetupCompleted,
    jumpTo,
    reinitiateWorkflow,
    selectedProfileKey,
    onLoadProfile,
  ]);

  const {
    profileDeleteConfirmationDialog,
    onSaveProfile,
    onSaveProfileAs,
    onRenameProfile,
    onDeleteProfile,
    onDeleteProfiles,
  } = useProfileCrudActions({
    selectedProfileKey,
    selectedSettingsType,
    lastAppliedProfileKey,
    profiles,
    selectedProfileKeyRef,
    flowSnapshotPersistingRef,
    flowSnapshotSupersededRef,
    setSelectedProfileKey,
    setSelectedSettingsType,
    setLastAppliedProfileKey,
    setIsSaving,
    setSaveError,
    setProfileError,
    saveConfiguration,
    markWorkflowApplied,
    loadProfiles,
    onProfileConfirmed,
  });

  return {
    isSaving,
    saveError,
    setSaveError,
    profiles,
    profilesLoading,
    selectedProfileKey,
    lastAppliedProfileKey,
    profileError,
    selectedSettingsType,
    profileApplyProgress,
    profileDeleteConfirmationDialog,
    clearProfileApplyProgress,
    onSelectedProfileChange: handleSelectedProfileChange,
    onSelectedSettingsTypeChange: setSelectedSettingsType,
    onRefreshProfiles: loadProfiles,
    onLoadProfile,
    onSaveProfile,
    onSaveProfileAs,
    onRenameProfile,
    onDeleteProfile,
    onDeleteProfiles,
    handleApply,
    handleContinue,
    handleReinitiateWorkflow,
  };
};
